/**
 * GDPR/ePrivacy cookie-consent banner (vanilla-cookieconsent v3) wired to
 * Google Consent Mode v2.
 *
 * Flow: index.html / gtag-init.js sets all consent signals to "denied" before
 * GA4 fires. When the user accepts the Analytics category here, we call
 * gtag('consent','update', { analytics_storage: 'granted' }), mirror the choice
 * into localStorage (so repeat visits pre-grant before this JS loads — see
 * gtag-init.js), and emit a window event so Vercel Analytics can mount.
 * Microsoft Clarity shares the category but not the mechanism: its tag is
 * injected from here on acceptance rather than shipped in index.html.
 *
 * Renders no DOM itself — the library injects its own modals/portals; this
 * component only drives the lifecycle. Re-open the preferences modal anywhere
 * via `showPreferences()` from "vanilla-cookieconsent" (used in the footer).
 */
import { useEffect } from "react";
import { run, acceptedCategory } from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import "./cookie-consent-theme.css";
import { ANALYTICS_CONSENT_KEY, CONSENT_CHANGED_EVENT } from "@/lib/consent";
import { isAnalyticsSuppressed } from "@/lib/analytics";
import { enableClarity, disableClarity } from "@/lib/clarity";

type GtagFn = (...args: unknown[]) => void;

/** Push the current analytics choice to Consent Mode v2, localStorage and the app. */
function syncConsent() {
  const analyticsGranted = acceptedCategory("analytics");

  // Super admins never get analytics_storage granted (onConsent re-fires this
  // on every load for returning accepters). Their human consent choice is
  // still mirrored below so it survives the opt-out flag being cleared later.
  const analyticsAllowed = analyticsGranted && !isAnalyticsSuppressed();

  const gtag = (window as unknown as { gtag?: GtagFn }).gtag;
  if (typeof gtag === "function") {
    gtag("consent", "update", {
      analytics_storage: analyticsAllowed ? "granted" : "denied",
    });
  }

  try {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, analyticsGranted ? "granted" : "denied");
  } catch {
    /* localStorage blocked — Consent Mode default still protects the user */
  }

  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGED_EVENT, { detail: { analytics: analyticsGranted } }),
  );

  // Microsoft Clarity (heatmaps + session recordings) sits behind the same
  // category: unlike GA4 its tag isn't in index.html at all, so this call is
  // what puts it on the page — and withdrawal drops its cookies and stops it.
  // Deliberately last, and caught: these calls hand control to Microsoft's
  // recorder, and a throw in there must not cost us the consent mirror or the
  // event above (Vercel Analytics mounts off it).
  try {
    if (analyticsAllowed) {
      enableClarity();
    } else {
      disableClarity();
    }
  } catch {
    /* third-party recorder failed to start — consent state is already applied */
  }
}

export function CookieConsent() {
  useEffect(() => {
    void run({
      guiOptions: {
        consentModal: { layout: "box", position: "bottom left", equalWeightButtons: true },
        preferencesModal: { layout: "box", equalWeightButtons: true },
      },
      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {},
      },
      language: {
        default: "en",
        translations: {
          en: {
            consentModal: {
              title: "We value your privacy",
              description:
                "EXOS uses essential cookies to run the site and cookieless Vercel analytics to measure usage. With your consent, we also enable Google Analytics and Microsoft Clarity — which records how pages are used, including heatmaps and session replays — to better understand how the product is used. You can change your choice anytime via “Cookie settings” in the footer.",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Manage preferences",
              footer: '<a href="/privacy">Privacy Policy</a>\n<a href="/terms">Terms</a>',
            },
            preferencesModal: {
              title: "Cookie preferences",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save preferences",
              closeIconLabel: "Close",
              sections: [
                {
                  title: "Essential",
                  description:
                    "Required for core functionality such as authentication and security. These are always on.",
                },
                {
                  title: "Analytics",
                  description:
                    "Help us understand how the product is used via Google Analytics 4 and Microsoft Clarity, which captures heatmaps and session replays of your visit (form fields and other sensitive text are masked). Both load only with your consent, and rejecting deletes their cookies. (Vercel Web Analytics and Speed Insights are cookieless and run without storing data on your device.)",
                  linkedCategory: "analytics",
                },
                {
                  title: "More information",
                  description:
                    'For details on how we process data, see our <a href="/privacy">Privacy Policy</a>.',
                },
              ],
            },
          },
        },
      },
      onFirstConsent: syncConsent,
      onConsent: syncConsent,
      onChange: syncConsent,
    });
  }, []);

  return null;
}
